import React, { useContext, useState } from 'react';
import moment from 'moment';
import 'moment/locale/sv';
import calendarIcon from '../images/calendar-line.svg';
import clockIcon from '../images/clock-line.svg';
import sessionIcon from '../images/stats-line.svg';
import pagesIcon from '../images/file-line.svg';
import startIcon from '../images/play-button.svg';
import stopIcon from '../images/stop-button.svg';
import bookmarkPlatinum from '../images/bookmark-platinum.svg';
import bookmarkGold from '../images/bookmark-gold.svg';
import bookmarkSilver from '../images/bookmark-silver.svg';
import bookmarkBronze from '../images/bookmark-bronze.svg';
import bookmarkDefault from '../images/bookmark-default.svg';
import bookmarkFilled from '../images/bookmark-default-filled.svg';
import bookCover from '../images/empty-book.svg';
import { PersistentContext } from './PersistentContext';
import {
  formatTime,
  prettySecondsToTime,
  updateProtocol,
} from '../utils/functions';
import {
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import { analytics, db } from '../firebaseConfig';
import BookDetails from './BookDetails';
import { useTranslation } from '../i18n';
import BookProgress from './BookProgress';
import AddMissingBookInfo from './AddMissingBookInfo';
import { logEvent } from 'firebase/analytics';

const CurrentlyReading = ({
  user,
  userData,
  books,
  fetchBooks,
  fetchUserData,
  showNotification,
  updateCurrentlyReading,
}) => {
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();
  const { globalState, setGlobalState } = useContext(PersistentContext);
  const time = globalState.duration || 0;
  const isRunning = globalState.isRunning || false;
  const startTime = globalState.startTime || null;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentBook, setCurrentBook] = useState(null);

  const bookID = userData?.currentlyReading?.bookID;

  const handleStart = async (book, manual = false) => {
    if (
      !book.title ||
      book.author_name?.length === 0 ||
      !book.number_of_pages ||
      book.number_of_pages === 0
    ) {
      setCurrentBook(book);
      setDialogOpen(true);
      return;
    }

    if (time === 0) {
      logEvent(analytics, 'reading_session_start');
    }

    if (book.bookID !== bookID) {
      // Start a new session for a book that's not currently being read
      // TODO: Add a warning that current session will be lost
      const userRef = doc(db, 'Users', user.uid);

      try {
        await runTransaction(db, async (transaction) => {
          await transaction.update(userRef, {
            currentlyReading: {
              bookID: book.bookID,
              title: book.title,
              author_name: book.author_name,
              isbn: book.isbn,
              number_of_pages: book.number_of_pages || 0,
              publish_date: book.publish_date || '',
              publisher: book.publisher || '',
              cover_url: book.cover_url || [false],
              pages: book.pages || 0,
              time: book.time || 0,
            },
          });
        });

        await fetchUserData(
          user.email,
          false,
          'handleStart, CurrentlyReading.js',
        ).then(() => {
          // Reset globalState after the transaction and data fetch have completed
          setGlobalState({
            ...globalState,
            duration: 0,
            startTime: new Date().getTime(),
            sessionDuration: 0,
            isRunning: !manual,
            activeSession: !manual,
            manualSession: manual,
            endPage: book.pages || 0,
          });
        });
      } catch (error) {
        console.error('Error updating user stats: ', error);
      }
    } else {
      if (!isRunning) {
        // Start a new session for currentlyReading book
        setGlobalState({
          ...globalState,
          duration: 0,
          startTime: new Date().getTime(),
          sessionDuration: 0,
          isRunning: !manual,
          activeSession: !manual,
          manualSession: manual,
          endPage: book.pages || 0,
        });
      } else {
        // Stop  ongoing session
        handleStop(manual);
      }
    }
  };

  const handleStop = (manual = false) => {
    const now = new Date().getTime();
    const currentSessionDuration = Math.floor((now - startTime) / 1000);

    setGlobalState((prevState) => ({
      ...prevState,
      isRunning: false,
      activeSession: !manual,
      manualSession: manual,
      sessionDuration: prevState.sessionDuration + currentSessionDuration,
      duration: prevState.sessionDuration + currentSessionDuration, // Ensure duration is up to date when stopping
    }));
  };

  const handleUpdateBook = async (bookData) => {
    try {
      await updateUserBooks(bookData);
      await updateUsers(bookData);
    } catch (error) {
      console.error('Error updating book or user info: ', error);
    } finally {
      finalizeUpdate();
    }
  };

  const updateUserBooks = async (bookData) => {
    const q = query(
      collection(db, 'UserBooks'),
      where('bookID', '==', bookData.bookID),
      where('userID', '==', bookData.userID),
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const docRef = doc.ref;

      const updateData = buildUpdateData(bookData);
      await updateDoc(docRef, updateData);
    } else {
      console.error('No matching document found in UserBooks!');
    }
  };

  const updateUsers = async (bookData) => {
    const r = query(
      collection(db, 'Users'),
      where('uid', '==', bookData.userID),
    );
    const userDataQuerySnapshot = await getDocs(r);

    if (!userDataQuerySnapshot.empty) {
      const doc = userDataQuerySnapshot.docs[0];
      const docRef = doc.ref;

      const updateData = buildNestedUpdateData(bookData);
      await updateDoc(docRef, updateData);
    } else {
      console.error('No matching document found in Users!');
    }
  };

  const buildUpdateData = (bookData) => {
    const updateData = {};

    if (bookData.title) updateData.title = bookData.title;
    if (bookData.author) updateData.author_name = [bookData.author];
    if (bookData.pages) updateData.number_of_pages = parseInt(bookData.pages);

    return updateData;
  };

  const buildNestedUpdateData = (bookData) => {
    const updateData = {};

    if (bookData.title) updateData['currentlyReading.title'] = bookData.title;
    if (bookData.author)
      updateData['currentlyReading.author_name'] = [bookData.author];
    if (bookData.pages)
      updateData['currentlyReading.number_of_pages'] = parseInt(bookData.pages);

    return updateData;
  };

  const finalizeUpdate = () => {
    fetchBooks();
    fetchUserData(user.email, false, 'handleUpdateBook, CurrentlyReading.js');
    setDialogOpen(false);
    setCurrentBook(null);
    showNotification(t.bookupdatedforsession);
  };

  const bookmarkUser = () => {
    let bookmark;
    switch (userData.bookmark) {
      case 'platinum':
        bookmark = bookmarkPlatinum;
        break;
      case 'gold':
        bookmark = bookmarkGold;
        break;
      case 'silver':
        bookmark = bookmarkSilver;
        break;
      case 'bronze':
        bookmark = bookmarkBronze;
        break;
      default:
        bookmark = bookmarkFilled;
        break;
    }
    return bookmark;
  };

  return (
    books?.length > 0 && (
      <div className="card-wrapper">
        {typeof books !== 'undefined' &&
          books
            .filter((book) => book.status === 'reading')
            .sort((a, b) => {
              if (a.bookID === bookID) return -1; // Place a first if it matches bookID
              if (b.bookID === bookID) return 1; // Place b first if it matches bookID
              // If neither a nor b matches bookID, sort by createdAt
              return new Date(a.createdAt) - new Date(b.createdAt);
            })
            .map((book, index) => (
              <div key={book.id} className="card book-card">
                <div className="card-large">
                  <img
                    src={updateProtocol(book.cover_url[0]) || bookCover}
                    alt={book.title}
                    className="card-large-image"
                    height="auto"
                    width="auto"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  <div className="title-card">
                    <div className="fs-xl">{book.title}</div>
                    <div className="fs-l">{book.author_name}</div>
                    <div className="session-wrapper">
                      <button
                        disabled={isRunning && bookID !== book.bookID}
                        className="btn btn-icon"
                        onClick={() => handleStart(book)}
                      >
                        <img
                          className="icon"
                          src={
                            bookID == book.bookID && isRunning
                              ? stopIcon
                              : startIcon
                          }
                          width={32}
                          height={32}
                          alt="Session start icon"
                        />
                        {bookID == book.bookID && isRunning
                          ? formatTime(time)
                          : t.startsession}
                      </button>
                      <button
                        disabled={
                          (isRunning && bookID !== book.bookID) || isRunning
                        }
                        className="btn btn-sm btn-borderless fs-s"
                        onClick={() => handleStart(book, true)}
                      >
                        {t.manualsession}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="marked-reading">
                  <img
                    className={bookID !== book.bookID ? 'pt-65' : undefined}
                    src={
                      bookID === book.bookID ? bookmarkUser() : bookmarkDefault
                    }
                    width={32}
                    height={42}
                    alt="Bookmark icon"
                  />
                </div>

                <BookProgress book={book} />
                <div className="btn-group-right">
                  <div className="reading-stats">
                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={calendarIcon}
                        width={20}
                        height={20}
                        alt="Calendar icon"
                      />
                      {book.dateStarted
                        ? moment(book.dateStarted.toDate())
                            .locale(lang)
                            .format('D MMM, YYYY')
                        : t.notStarted}
                    </div>

                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={clockIcon}
                        width={20}
                        height={20}
                        alt="Clock icon"
                      />
                      {prettySecondsToTime(book.time || 0)}
                    </div>
                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={sessionIcon}
                        width={20}
                        height={20}
                        alt="Session icon"
                      />
                      {book.sessions?.length || 0}{' '}
                      {book.sessions?.length !== 1
                        ? t.sessionsLow
                        : t.sessionLow}
                    </div>
                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={pagesIcon}
                        width={20}
                        height={20}
                        alt="Pages icon"
                      />
                      {book.pages || 0}{' '}
                      {book.pages !== 1 ? t.pagesLow : t.pageLow}
                    </div>
                  </div>
                  <BookDetails
                    user={user}
                    book={book}
                    buttonClass={'btn btn-sm btn-primary'}
                    fetchBooks={fetchBooks}
                    fetchUserData={fetchUserData}
                    showNotification={showNotification}
                    updateCurrentlyReading={updateCurrentlyReading}
                  />
                </div>
              </div>
            ))}
        {currentBook && (
          <AddMissingBookInfo
            currentBook={currentBook}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSave={(bookData) =>
              handleUpdateBook({
                ...bookData,
                bookID: currentBook.bookID,
                userID: currentBook.userID,
              })
            }
          />
        )}
      </div>
    )
  );
};

export default CurrentlyReading;
